import React, { useEffect, useState } from 'react'

import { BUTTON_THEME, CircularLoader, Button } from '@st-operations/uikit/src/v2/index.js'

import User from '_lib/User'
import arrayDiff from '_lib/arrayDiff'
import { logErrorAsync } from '_lib/utility'

import Header from '_components/header/index.jsx'
import Table from '_components/table'
import ContentRuleChangeRequestsStatusBadge from '_components/content-rule-change-request-status-badge'

import {
  getDestination,
  getDestinationChangeRequest,
  updateDestination,
  updateDestinationChangeRequest,
} from '_services/destinations'

import './styles.scss'

const valueToString = (property) => {
  let text
  if (!property) {
    text = `(empty)`
  }
  if (Array.isArray(property)) {
    text = JSON.stringify(property.map((m) => m.name || m))
  } else if (typeof property === 'object') {
    text = JSON.stringify(property.name || property)
  } else if (typeof property === 'string') {
    text = `"${property}"`
  } else {
    text = `${property}`
  }
  return text
}

const DestinationChangeRequest = (props) => {
  const { id } = props.match.params

  const [original, setOriginal] = useState()
  const [changeRequest, setChangeRequest] = useState()
  const [inRequest, setInRequest] = useState(false)
  const [allowedToUpdate, setAllowedToUpdate] = useState(false)

  const differentProperties = Object.keys(changeRequest?.difference || {})

  useEffect(() => {
    User.watch(this, (user) => {
      if (user) {
        setAllowedToUpdate(
          user?.datasphere_permissions?.destinations?.permissions?.configuration
            ?.allowed_to_update_destinations
        )
      }
    })

    const load = async (id) => {
      try {
        const changeRequestData = await getDestinationChangeRequest(id)
        setChangeRequest(changeRequestData)
        const originalData = await getDestination(changeRequestData.destination._id)
        setOriginal(originalData)
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
    }

    load(id)
  }, [id])

  const handleSaveAs = async (status = 'reject', customRoute = false, baseObject) => {
    if (inRequest) return
    setInRequest('Updating')

    function changeStatus(status) {
      return {
        ...(baseObject || changeRequest),
        status,
      }
    }

    try {
      if (status === 'approved') {
        await updateDestination(original._id, {
          ...original,
          ...changeRequest.difference,
        })
      }
      const requestedChangeStatus = changeStatus(status)
      await updateDestinationChangeRequest(id, requestedChangeStatus)

      if (!customRoute) {
        toast.success('Update successful!')
        handleExit()
      }
      return true
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    } finally {
      setInRequest(false)
    }
  }

  const handleExit = () => {
    props.history.push(`/destination-change-requests`)
  }

  if (!changeRequest) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <CircularLoader />
      </div>
    )
  }

  if (!original || !changeRequest.difference) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <CircularLoader />
      </div>
    )
  }

  return (
    <div className="destination-change-request">
      <Header
        title={
          <div className="flex flex-row gap-4 items-center">
            <span>{original.name}</span>
            <ContentRuleChangeRequestsStatusBadge status={changeRequest.status} />
          </div>
        }
      >
        <Button theme={BUTTON_THEME.GHOST} onClick={handleExit}>
          Exit
        </Button>
        <If condition={changeRequest.status === 'pending' && allowedToUpdate}>
          <Button theme={BUTTON_THEME.SECONDARY} onClick={() => handleSaveAs('rejected')}>
            Reject
          </Button>
          <Button
            theme={BUTTON_THEME.PRIMARY}
            onClick={async () => {
              handleSaveAs('approved')
            }}
          >
            Approve
          </Button>
        </If>
      </Header>
      <Table
        columns={[
          {
            name: 'Property',
            key: 'property',
          },
          {
            name: 'New',
            key: 'newValue',
            // eslint-disable-next-line react/display-name
            render: (item) => {
              let originalValue = item.originalValue
              const originalLineBreakSplit =
                (typeof originalValue === 'string' && originalValue?.split('\n')) || []
              if (originalLineBreakSplit.length > 1) {
                originalValue = originalLineBreakSplit
              }

              let newValue = item.newValue
              const newLineBreakSplit =
                (typeof newValue === 'string' && newValue?.split('\n')) || []
              if (newLineBreakSplit.length > 1) {
                newValue = newLineBreakSplit
              }

              if (Array.isArray(newValue)) {
                const originalValueArray = Array.isArray(originalValue) ? originalValue : []
                const { added, removed } = arrayDiff(originalValueArray, newValue)
                let render = []
                if (added.length > 0) {
                  render.push(
                    <ul className="text-green-500 font-bold list-none">
                      {added.map((value) => (
                        <li key={value}>+ {valueToString(value)}</li>
                      ))}
                    </ul>
                  )
                }
                if (removed.length > 0) {
                  render.push(
                    <ul className="text-red-600 font-bold list-none">
                      {removed.map((value) => (
                        <li key={value}>- {valueToString(value)}</li>
                      ))}
                    </ul>
                  )
                }

                if (added.length > 0 || removed.length > 0) {
                  return render
                }
              }
              return valueToString(newValue)
            },
          },
        ]}
        data={differentProperties.map((property) => ({
          property,
          originalValue: original?.[property],
          newValue: changeRequest.difference[property],
        }))}
      />
    </div>
  )
}

export default DestinationChangeRequest
